import classNames from "components/ui/classNames";
import { RadioGroup, RadioGroupItem } from "components/ui/RadioGroup";
import type { YAxisOptions } from "highcharts";
import { GitCommitIcon, SplineIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useIntl } from "react-intl";

export type AxisScaleType = NonNullable<YAxisOptions["type"]>;
type AxisScale = {
  type: AxisScaleType;
  label: string;
  icon?: ReactElement;
  tooltipContent?: string;
  disabled?: boolean;
};
interface AxisScaleSelectorProps {
  scales: AxisScale[];
  value: AxisScaleType;
  onChange: (scaleType: AxisScaleType) => void;
}
export default function AxisScaleSelector({ scales, value, onChange }: AxisScaleSelectorProps) {
  return (
    <RadioGroup
      loop={false}
      className={classNames(
        "grid w-full grid-cols-2 justify-between rounded-2xl border border-slate-200 bg-slate-50/60 p-1",
        "sm:flex sm:w-auto sm:max-w-xs sm:justify-normal sm:space-x-2",
        "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
      )}
      orientation="horizontal"
      value={value}
      onValueChange={(value) => {
        const match = scales.find((candidate) => candidate.type === value);
        if (match) {
          onChange(match.type);
        }
      }}
    >
      {scales.map(({ type, label, icon, tooltipContent, disabled }) => {
        return (
          <RadioGroupItem
            key={type}
            value={type}
            disabled={disabled}
            title={tooltipContent || label}
            className={classNames(
              "h-9 w-full items-center justify-center rounded-xl p-1 text-center sm:w-10",
              "border border-transparent text-sm font-medium tracking-wide text-slate-500",
              "data-[state=checked]:bg-violet-50 data-[state=checked]:font-bold data-[state=checked]:text-violet-600",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-100 focus-visible:ring-offset-1 focus-visible:ring-offset-gray-300",
              "data-[state=checked]:focus-visible:rounded-xl data-[state=checked]:focus-visible:bg-gray-50 data-[state=checked]:focus-visible:text-gray-600",
            )}
          >
            {icon || label}
          </RadioGroupItem>
        );
      })}
    </RadioGroup>
  );
}

export function LogLinearScaleSelector(props: Omit<AxisScaleSelectorProps, "scales">) {
  const intl = useIntl();
  return (
    <AxisScaleSelector
      scales={[
        {
          type: "linear",
          label: intl.formatMessage({
            id: "0zadUl",
            defaultMessage: "Linear",
            description: "Tipo de escala do gráfico",
          }),
          icon: <GitCommitIcon className="h-6 w-6" strokeWidth={2} />,
        },
        {
          type: "logarithmic",
          label: intl.formatMessage({
            id: "Ty6wag",
            defaultMessage: "Logarítimica",
            description: "Tipo de escala do gráfico",
          }),
          icon: <SplineIcon className="h-6 w-6" strokeWidth={2} />,
        },
      ]}
      {...props}
    />
  );
}
